import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  InventoryStatus,
  InitialETADuration,
  GateReleaseThreshold,
  TimeOnSiteDuration,
  CurrentLocationType,
} from "api/consts";
import { logWarningForMissingTranslation } from "utils/log-warning.utils";

const Lads = {
  PLANT: "Plant",
  UPFITTER: "Upfitter",
  PORT: "Port",
  DISTRIBUTION: "Distribution",
};

export const useInventoryViewTranslation = () => {
  const { t } = useTranslation("inventory-view");

  const getTranslatedStatus = useCallback(
    (option: string) => {
      switch (option) {
        case InventoryStatus.SHIPPABLE:
          return t("inventory-view:Shippable");
        case InventoryStatus.NONSHIPPABLE:
          return t("inventory-view:Non-Shippable");
        default: {
          logWarningForMissingTranslation(option);
          return option;
        }
      }
    },
    [t],
  );

  const getTranslatedInitialETALabels = useCallback(
    (option: string) => {
      switch (option) {
        case InitialETADuration.MISSED_FIRST_ETA:
          return t("inventory-view:Missed First ETA");
        case InitialETADuration.HOURS_0_47_OUT:
          return t("inventory-view:0-47 Hours Out");
        case InitialETADuration.HOURS_48_95_OUT:
          return t("inventory-view:48-95 Hours Out");
        case InitialETADuration.HOURS_96_OUT:
          return t("inventory-view:96+ Hours Out");
        case InitialETADuration.BEYOND_PAST_72_HOURS:
          return t("inventory-view:72+ Hours Past");
        case InitialETADuration.WITHIN_PAST_72_HOURS:
          return t("inventory-view:1-72 Hours Past");
        default: {
          logWarningForMissingTranslation(option);
          return option;
        }
      }
    },
    [t],
  );

  const getTranslatedGateReleaseThresholdLabels = useCallback(
    (option: string) => {
      switch (option) {
        case GateReleaseThreshold.DAYS_AGO_150:
          return t("inventory-view:150+ Days ago");
        case GateReleaseThreshold.DAYS_AGO_100_149:
          return t("inventory-view:100-149 Days ago");
        case GateReleaseThreshold.DAYS_AGO_50_99:
          return t("inventory-view:50-99 Days ago");
        case GateReleaseThreshold.DAYS_AGO_0_49:
          return t("inventory-view:0-49 Days ago");
        default: {
          logWarningForMissingTranslation(option);
          return option;
        }
      }
    },
    [t],
  );

  const getTranslatedTimeOnSiteLabels = useCallback(
    (option: string) => {
      switch (option) {
        case TimeOnSiteDuration.DAYS_10:
          return t("inventory-view:10+ Days");
        case TimeOnSiteDuration.DAYS_6_9:
          return t("inventory-view:6-9 Days");
        case TimeOnSiteDuration.DAYS_3_5:
          return t("inventory-view:3-5 Days");
        case TimeOnSiteDuration.DAYS_0_2:
          return t("inventory-view:0-2 Days");
        default: {
          logWarningForMissingTranslation(option);
          return option;
        }
      }
    },
    [t],
  );

  const getTranslatedLabelsPipelineGraph = useCallback(
    (option: string) => {
      switch (option.replace(/ /g, "").toLowerCase()) {
        case CurrentLocationType.AT_LOCATION:
          return t("inventory-view:At Location");
        case CurrentLocationType.ON_RAIL:
          return t("inventory-view:On Rail");
        case CurrentLocationType.ON_THE_ROAD:
          return t("inventory-view:On the Road");
        case CurrentLocationType.ON_THE_WATER:
          return t("inventory-view:On the Water");
        case Lads.PLANT:
          return t("lads:At Plant");
        case Lads.UPFITTER:
          return t("lads:At Upfitter");
        case Lads.PORT:
          return t("lads:At Port");
        case Lads.DISTRIBUTION:
          return t("lads:At Distribution");
        default: {
          logWarningForMissingTranslation(option);
          return option;
        }
      }
    },
    [t],
  );

  return {
    getTranslatedStatus,
    getTranslatedInitialETALabels,
    getTranslatedTimeOnSiteLabels,
    getTranslatedGateReleaseThresholdLabels,
    getTranslatedLabelsPipelineGraph,
  };
};
